import React, { forwardRef, useState } from 'react';
import { useRegistrationCoreTenders } from '~src/hooks/api-hooks/useRegistrationCoreTenders';
import {
  PrintTableItem,
  TendersPrintTable,
} from '~components/molecules/Tabs/TenderTabs/components/Tenders/TendersPrintTable';
import { loadTendersRegistrationCoreTenderFolders } from '~src/services/api';
import { ArchiveRegistrationCore, TenderSubFolderCoreVM } from '~src/types/db';
import { useSharedTenderFolders } from '~src/hooks/api-hooks/useSharedTenderFolders';
import { Button, Empty, Spin } from 'antd';
import { useIntl } from 'react-intl';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from '~components/molecules/DetailsLayouts/components/LeftDetailsBlock/LeftDetailsBlock.style';

export type TenderDocumentPrintProps = {
  projectId: string;
  tenderId: string;
  isStartFetch?: boolean;
  isExpandable?: boolean;
};

const TenderDocumentExpandable = forwardRef<
  HTMLDivElement,
  TenderDocumentPrintProps
>(({ projectId, tenderId, isStartFetch, isExpandable }, ref) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();

  const [isLoading, setIsLoading] = useState(true);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [itemsList, setItemsList] = useState<PrintTableItem[]>([]);

  const coreTenderRegistration = useRegistrationCoreTenders(
    {
      projectId,
      tenderId,
    },
    {
      retry: false,
      onSuccess: async (data) => {
        const folderList = await Promise.all(
          data.root_folders.map(async (folder) => {
            setExpandedRowKeys((prev) => [...prev, String(folder.id)]);

            const restSubfoldersList = await loadTendersRegistrationCoreTenderFolders(
              {
                projectId,
                tenderId,
                folderId: String(folder.id),
              }
            );

            const rootTenderFolder = restSubfoldersList.filter(
              ({ parent_id }) => !parent_id
            )[0];

            const treeFolder = createFolderStructure(
              restSubfoldersList,
              (key) => {
                setExpandedRowKeys((prev) => [...prev, key]);
              },
              {
                ...rootTenderFolder,
                name: folder.alias_title,
                children: [],
                isAttachment: false,
              }
              // restSubfoldersList.map((folder) => ({
              //   ...folder,
              //   parent_id: folder.parent_id || folder.id,
              // })),
            );

            return treeFolder;
          })
        );

        // console.log(`folderList`, folderList);

        setItemsList([
          ...folderList,
          ...data.attachments.map((attachment) => ({
            ...attachment,
            isAttachment: true,
          })),
        ]);

        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
        setItemsList([]);
      },
      // refetchOnMount: false,
      // enabled: isStartFetch,
    }
  );

  // const handleExpandableChange = (
  //   expanded: boolean,
  //   record: PrintTableItem
  // ) => {
  //   if (!isExpandable) return;
  //
  //   if (expanded) {
  //     setExpandedRowKeys((prev) => [...prev, String(record.id)]);
  //   } else {
  //     setExpandedRowKeys((prev) =>
  //       prev.filter((key) => key !== String(record.id))
  //     );
  //   }
  // };
  const EmptyBlock: React.FC = (props) => {
    return (
      <Empty
        css={{
          padding: '5vh 0',
          margin: '0',
        }}
        description={props.children}></Empty>
    );
  };

  /* PRINT ONLY LAYOUT COMPONENT */
  if (isLoading) {
    return (
      <div
        style={{
          padding: '5vh 0',
          display: 'flex',
          justifyContent: 'center',
        }}>
        <Spin size="large" />
      </div>
    );
  }

  if (itemsList.length === 0) {
    return <EmptyBlock>{intl.formatMessage({ id: 'no-files' })}</EmptyBlock>;
  }

  return (
    <TendersPrintTable ref={ref} itemsList={itemsList} isListLoading={false} />
  );
});

function createFolderStructure(
  folders: TenderSubFolderCoreVM,
  onAddToStructure: (key) => void,
  rootFolder: PrintTableItem
): PrintTableItem {
  const buildHierarchyFromRoot = (
    currentLevelRootFolder: PrintTableItem & {
      registrations?: ArchiveRegistrationCore[];
    }
  ) => {
    currentLevelRootFolder.children = [];

    const currentLevelChildren = folders.filter(
      ({ parent_id }) => parent_id === currentLevelRootFolder.id
    );

    for (const folder of currentLevelChildren) {
      onAddToStructure(String(folder.id));
      const childFolder = buildHierarchyFromRoot({
        ...folder,
        isAttachment: false,
        children: [],
      });
      currentLevelRootFolder.children.push(childFolder);

      // childFolder.children?.push(
      //   ...folder.registrations.map(({ title, description, id }) => ({
      //     id,
      //     name: title,
      //     description,
      //     isAttachment: true,
      //   }))
      // );
    }

    const currentFolderRegistrations = currentLevelRootFolder.registrations?.map(
      ({ title, description, id }) => ({
        id,
        name: title,
        description,
        isAttachment: true,
      })
    );

    currentLevelRootFolder.children.push(...(currentFolderRegistrations || []));

    return currentLevelRootFolder;
  };

  return buildHierarchyFromRoot(rootFolder);
}

export default TenderDocumentExpandable;
